import { useNavigate } from 'react-router';
import { ICandidateOnsiteInterviewEntity, OnsiteInterviewStatus } from '~/domain/PermanentJobs';
import { useConfirmInterview } from './useConfirmInterview';
import { useDeclineInterview } from './useDeclineInterview';

interface IUseOnsiteInterviewActionsPayload {
  maps: Record<string, ICandidateOnsiteInterviewEntity>;
  officeId: string;
  postingId: string;
  professionalId: string;
}

/**
 * What an office can do with one of a candidate's onsite interviews — open it,
 * confirm or decline an application, message the professional — shared by the
 * accordion and the Candidate Details list so both follow the same routes.
 *
 * Opening an application confirms it inline only when this candidate is the sole
 * applicant; with several, the office has to compare them on the job's own
 * confirm page.
 */
export const useOnsiteInterviewActions = ({
  maps,
  officeId,
  postingId,
  professionalId,
}: IUseOnsiteInterviewActionsPayload) => {
  const navigate = useNavigate();

  const confirm = useConfirmInterview({ professionalId, officeId });
  const decline = useDeclineInterview({ professionalId });

  const handleMessageClick = (id: string) => {
    navigate(`/dashboard/chat/${id}`);
  };

  const handleViewDetails = (id: string) => {
    const data = maps[id];

    if (!data || data.onsiteInterviewStatus !== OnsiteInterviewStatus.ActionNeeded) {
      navigate(`/dashboard/job/${id}`);
      return;
    }

    if (data.candidatesAppliedCount > 1) {
      navigate(`/dashboard/job/${id}/confirm/${professionalId}`);
      return;
    }

    if (data.counterOffer) {
      confirm.handleConfirmInterview(id, data.counterOffer);
    }
  };

  const handleCloseDeclineSuccessPopup = () => {
    decline.handleCloseDeclineInterview();
    navigate(`/permanent-jobs/job-overview/${postingId}`, { replace: true });
  };

  return {
    confirm,
    decline,
    handleCloseDeclineSuccessPopup,
    handleMessageClick,
    handleViewDetails,
  };
};

export type TOnsiteInterviewActions = ReturnType<typeof useOnsiteInterviewActions>;
